import { useState } from 'react'
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/solid'

const DEFAULT_CONFIG = {
  appearance: {
    primaryColor: '#6366f1',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    headerColor: '#6366f1',
    userMessageColor: '#6366f1',
    assistantMessageColor: '#f3f4f6',
    fontFamily: 'system-ui',
    borderRadius: 12,
  },
  branding: {
    chatbotName: 'Assistant',
    avatarUrl: null,
    headerTitle: 'Chat with us',
    inputPlaceholder: 'Type a message...',
  },
  behavior: {
    position: 'bottom-right',
  },
  messages: {
    welcomeMessage: 'Hi! How can I help you today?',
    suggestedQuestions: [],
  },
}

function mergeConfig(config) {
  return {
    appearance: { ...DEFAULT_CONFIG.appearance, ...config?.appearance },
    branding: { ...DEFAULT_CONFIG.branding, ...config?.branding },
    behavior: { ...DEFAULT_CONFIG.behavior, ...config?.behavior },
    messages: { ...DEFAULT_CONFIG.messages, ...config?.messages },
  }
}

export default function WidgetPreview({ config }) {
  const [view, setView] = useState('chat') // 'bubble' or 'chat'
  const merged = mergeConfig(config)

  const { appearance, branding, messages } = merged
  const avatarInitial = branding.chatbotName?.[0]?.toUpperCase() || 'A'

  // Determine text color for header based on header color brightness
  const getContrastColor = (hex) => {
    if (!hex || hex.length < 7) return '#ffffff'
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.6 ? '#1f2937' : '#ffffff'
  }

  const headerTextColor = getContrastColor(appearance.headerColor)
  const userMsgTextColor = getContrastColor(appearance.userMessageColor)

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-slate-700">
          Live Preview
        </label>
        <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded-lg">
          <button
            onClick={() => setView('bubble')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
              view === 'bubble'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Bubble
          </button>
          <button
            onClick={() => setView('chat')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
              view === 'chat'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Chat
          </button>
        </div>
      </div>

      <div
        className="bg-slate-100 rounded-xl p-6 flex items-end min-h-[480px]"
        style={{
          justifyContent:
            merged.behavior.position === 'bottom-left' ? 'flex-start' : 'flex-end',
        }}
      >
        {view === 'bubble' && (
          <div className="flex flex-col items-center space-y-2">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:scale-105 transition-transform"
              style={{
                backgroundColor: appearance.primaryColor,
                borderRadius: '50%',
              }}
            >
              <ChatBubbleLeftRightIcon
                className="h-6 w-6"
                style={{ color: getContrastColor(appearance.primaryColor) }}
              />
            </div>
          </div>
        )}

        {view === 'chat' && (
          <div
            className="w-full max-w-[380px] shadow-2xl overflow-hidden"
            style={{
              borderRadius: `${appearance.borderRadius}px`,
              fontFamily: appearance.fontFamily,
            }}
          >
            {/* Header */}
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: appearance.headerColor }}
            >
              <div className="flex items-center space-x-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor:
                      headerTextColor === '#ffffff'
                        ? 'rgba(255,255,255,0.2)'
                        : 'rgba(0,0,0,0.1)',
                  }}
                >
                  <span
                    className="font-bold text-sm"
                    style={{ color: headerTextColor }}
                  >
                    {avatarInitial}
                  </span>
                </div>
                <div>
                  <div
                    className="text-sm font-semibold"
                    style={{ color: headerTextColor }}
                  >
                    {branding.headerTitle}
                  </div>
                  <div
                    className="text-[11px] flex items-center space-x-1"
                    style={{ color: headerTextColor, opacity: 0.7 }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full inline-block"
                      style={{ backgroundColor: '#22c55e' }}
                    />
                    <span>Online</span>
                  </div>
                </div>
              </div>
              <button
                className="p-1 rounded-lg transition-colors"
                style={{
                  color: headerTextColor,
                  opacity: 0.6,
                }}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Messages area */}
            <div
              className="px-4 py-4 space-y-3"
              style={{
                backgroundColor: appearance.backgroundColor,
                minHeight: '260px',
              }}
            >
              {/* Assistant welcome message */}
              <div className="flex items-end space-x-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: appearance.headerColor }}
                >
                  <span
                    className="text-xs font-semibold"
                    style={{ color: getContrastColor(appearance.headerColor) }}
                  >
                    {avatarInitial}
                  </span>
                </div>
                <div
                  className="px-3 py-2 text-sm max-w-[75%]"
                  style={{
                    backgroundColor: appearance.assistantMessageColor,
                    color: appearance.textColor,
                    borderRadius: `${Math.max(appearance.borderRadius - 4, 4)}px`,
                    borderBottomLeftRadius: '4px',
                  }}
                >
                  {messages.welcomeMessage}
                </div>
              </div>

              {/* Suggested questions */}
              {messages.suggestedQuestions?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pl-9">
                  {messages.suggestedQuestions.slice(0, 3).map((q, i) => (
                    <button
                      key={i}
                      className="px-2.5 py-1 text-xs border rounded-full transition-colors"
                      style={{
                        borderColor: appearance.primaryColor + '40',
                        color: appearance.primaryColor,
                        backgroundColor: appearance.primaryColor + '08',
                      }}
                    >
                      {q.length > 30 ? q.slice(0, 30) + '...' : q}
                    </button>
                  ))}
                </div>
              )}

              {/* Sample user message */}
              <div className="flex justify-end">
                <div
                  className="px-3 py-2 text-sm max-w-[75%]"
                  style={{
                    backgroundColor: appearance.userMessageColor,
                    color: userMsgTextColor,
                    borderRadius: `${Math.max(appearance.borderRadius - 4, 4)}px`,
                    borderBottomRightRadius: '4px',
                  }}
                >
                  Hello! I have a question.
                </div>
              </div>

              {/* Sample assistant reply */}
              <div className="flex items-end space-x-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: appearance.headerColor }}
                >
                  <span
                    className="text-xs font-semibold"
                    style={{ color: getContrastColor(appearance.headerColor) }}
                  >
                    {avatarInitial}
                  </span>
                </div>
                <div
                  className="px-3 py-2 text-sm max-w-[75%]"
                  style={{
                    backgroundColor: appearance.assistantMessageColor,
                    color: appearance.textColor,
                    borderRadius: `${Math.max(appearance.borderRadius - 4, 4)}px`,
                    borderBottomLeftRadius: '4px',
                  }}
                >
                  Of course! I'd be happy to help. What would you like to know?
                </div>
              </div>
            </div>

            {/* Input area */}
            <div
              className="px-4 py-3 border-t"
              style={{
                backgroundColor: appearance.backgroundColor,
                borderColor: appearance.textColor + '15',
              }}
            >
              <div className="flex items-center space-x-2">
                <div
                  className="flex-1 px-3 py-2 rounded-lg text-xs"
                  style={{
                    backgroundColor: appearance.textColor + '08',
                    color: appearance.textColor + '60',
                  }}
                >
                  {branding.inputPlaceholder}
                </div>
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: appearance.primaryColor }}
                >
                  <PaperAirplaneIcon
                    className="h-3.5 w-3.5"
                    style={{ color: getContrastColor(appearance.primaryColor) }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
